//
// # Wire Drawings
//

// Local Imports
import { Point, graphics, model } from "GenAlphaModel";
import { Dots, DotParent } from "./dot";
import { EntityKind } from "../../entity";
import { MousePos } from "../../mousepos";
import { Args, Entity } from "../../tree";
import { TextLabel } from "../shared";
import { PathEntity } from "../../primitives";
import { DrawTo } from "../../draw";
import type { RedrawRequest } from "../../redraw";
import * as style from "../../style";
// Type-only — wires.ts already imports WireSegment from here.
import type { Wires } from "./wires";

// The standard display width for all wire segments.
// Wire width is not user-configurable in the GUI; this constant defines the canonical value.
// Schematics stored with a different width (e.g., width: 1 from manually-written JSON) will
// be normalized to this value at render time so they display consistently with GUI-drawn wires.
const WIRE_DISPLAY_WIDTH = 2;

// Radius for endpoint hit detection (in schematic units).
// Used for wire endpoint stretching - clicking within this distance of an endpoint
// will trigger stretch mode instead of selecting the whole wire.
// This is smaller than PIN_SNAP_RADIUS (15) to avoid accidentally triggering
// stretch mode when trying to click near a pin connection point.
const ENDPOINT_HIT_RADIUS = 10;
const ENDPOINT_HIT_RADIUS_SQ = ENDPOINT_HIT_RADIUS * ENDPOINT_HIT_RADIUS;

// Constructor args for WireSegment. Extends the base Entity args with a
// back-reference to the owning Wires collection so update() can keep the
// bucket index in sync.
export interface WireSegmentArgs extends Args<model.WireSegment> {
  wires: Wires;
}

export class WireSegment
  extends Entity<model.WireSegment>
  implements DotParent
{
  readonly entityKind = EntityKind.WireSegment;

  // Back-reference to the owning Wires collection. Used by `update()` to keep
  // the bucket index in sync when endpoints change.
  readonly wires: Wires;

  constructor(args: WireSegmentArgs) {
    super(args);
    this.wires = args.wires;
    this.build();
  }
  private pathEntity!: PathEntity;
  public labels = new Set<WireLabel>();
  readonly dots = new Dots();

  // Keep the Wires bucket index in sync when endpoints change. Without this,
  // the bucket key (set at insertion) drifts away from the wire's actual
  // coordinates and Wires.delete can no longer find the wire.
  //
  // Skip provisional wires: they aren't in any bucket yet, and commitWire is
  // what places them.
  override update(data: Partial<model.WireSegment>): RedrawRequest {
    const reindex =
      data.endpoints !== undefined && !this.wires.provisionals.has(this);
    if (reindex) this.wires.rawDelete(this);
    const result = super.update(data);
    if (reindex) this.wires.rawAdd(this);
    return result;
  }

  start(): Point {
    return Point.clone(this.data.endpoints[0]);
  }
  end(): Point {
    return Point.clone(this.data.endpoints[1]);
  }
  // Bounding box from wire endpoints
  get bbox(): model.Bbox {
    return model.Bbox.fromTwoPoints([this.start(), this.end()]);
  }
  pathData(): graphics.Path {
    const { style, endpoints: points } = this.data;
    return graphics.Path.new({ points, style });
  }

  makeLabel = (text: graphics.Text): WireLabel => {
    return new WireLabel(
      new TextLabel({ data: { ...text, labelKind: "wire" }, parent: this }),
      this
    );
  };

  override build = () => {
    this.pathEntity = new PathEntity({
      data: this.pathData(),
      parent: this,
      // Apply the canonical wire display width to the style table.
      // We use WIRE_DISPLAY_WIDTH rather than this.data.style.width to ensure consistent
      // rendering regardless of what is stored in the schematic data. Wire widths are not
      // user-configurable, so the stored value is ignored in favor of the canonical constant.
      table: { ...wireShapeStyleTable, linewidth: WIRE_DISPLAY_WIDTH },
    });
    this.labels = new Set(this.data.labels.map(this.makeLabel));
  };

  // // Create from a list of `Point`s. Primarily creates the drawn `Path`.

  redraw = (to: DrawTo): unknown => {
    this.labels.forEach((l) => l.redraw(to));
    return this.pathEntity.redraw(to);
  };

  // Abort drawing an in-progress wire.
  abort = () => {};

  // Boolean indication of whether `point` lands on the wire. i.e. on any of its segments.
  hitTest = (mousePos: MousePos): boolean => {
    // FIXME: learn to test non-Manhattan wires
    const manhattanSegments = model.Manhattan.calc([...this.data.endpoints]);
    if (!manhattanSegments) return false;
    return manhattanSegments.some((segment) =>
      hitTestDrawnSegment(segment, mousePos.sch)
    );
  };

  // Hit test against wire endpoints for stretching.
  // Returns 0 if near start, 1 if near end, null if neither endpoint is hit.
  // If both endpoints are within the hit radius, returns the closer one.
  hitTestEndpoint = (mousePos: MousePos): 0 | 1 | null => {
    const pt = mousePos.sch;
    const startPt = this.start();
    const endPt = this.end();

    // Calculate squared distances to each endpoint (avoids sqrt for performance)
    const dxStart = pt.x - startPt.x;
    const dyStart = pt.y - startPt.y;
    const distStartSq = dxStart * dxStart + dyStart * dyStart;

    const dxEnd = pt.x - endPt.x;
    const dyEnd = pt.y - endPt.y;
    const distEndSq = dxEnd * dxEnd + dyEnd * dyEnd;

    const startInRange = distStartSq <= ENDPOINT_HIT_RADIUS_SQ;
    const endInRange = distEndSq <= ENDPOINT_HIT_RADIUS_SQ;

    // If both in range, return the closer one
    if (startInRange && endInRange) {
      return distStartSq <= distEndSq ? 0 : 1;
    }
    if (startInRange) return 0;
    if (endInRange) return 1;
    return null;
  };
}

export class WireLabel {
  readonly entityKind = EntityKind.WireLabel;
  constructor(public inner: TextLabel, public wire: WireSegment) {}

  // Forward to our inner `TextLabel`
  hitTest = (mousePos: MousePos) => this.inner.hitTest(mousePos);
  redraw = (to: DrawTo): unknown => this.inner.redraw(to);
  highlight = (state: boolean): unknown => this.inner.highlight(state);
}

// Wrapper for hit-testing the pointer against drawn wire segements,
// with tolerance equal to their drawn width.
const hitTestDrawnSegment = (
  seg: model.ManhattanSegment,
  pt: Point
): boolean => {
  const HIT_TEST_WIDTH = 5; // Equal to half the drawn width.
  return model.Manhattan.hitTest(seg, pt, HIT_TEST_WIDTH);
};

const wireColors: [style.Color, style.Color, style.Color, style.Color] = [
  style.Color.blue,
  style.Color.red,
  style.Color.new(37, 150, 190),
  style.Color.red,
];
const wireShapeStyleTable: style.ShapeStyleTable = {
  //       light light    dark  dark
  //       no    hilited  no    hilited
  fill: style.Special.None,
  stroke: wireColors,
  linewidth: 0, // This value is never used - wire widths are set by WIRE_DISPLAY_WIDTH in build()
  cap: "round",
  join: "round",
};
